import React, { useState, useEffect, useContext } from "react";
import { EDSButton } from "../../../../../eds/EDSButton";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import { Copy, Check, ExternalLink, Loader2 } from "lucide-react";
import { getCustomerPreOrderUrl } from "../../../../../../APIs/Booking/CustomerPreorderOperations";
import { AppContext } from "../../../../../../context/AppContext";
import useClipboard from "../../../../../../hooks/useClipboard";
import { openExternalLink } from "../../../../../../utils/nativeUtils/openExternalLink";

interface CustomerPreorderLinksProps {
  bookingId: string;
}

const CustomerPreorderLinks: React.FC<CustomerPreorderLinksProps> = ({
  bookingId
}) => {
  const { venueGroupConfig } = useContext(AppContext);
  const [leadBookerUrl, setLeadBookerUrl] = useState<string>("");
  const [groupMemberUrl, setGroupMemberUrl] = useState<string>("");
  const [isLoadingLeadBooker, setIsLoadingLeadBooker] = useState(false);
  const [isLoadingGroupMember, setIsLoadingGroupMember] = useState(false);

  const leadBookerClipboard = useClipboard();
  const groupMemberClipboard = useClipboard();

  // Fetch URLs when bookingId or collins_features change
  // Fetch logic moved inside useEffect to avoid callback dependency issues
  useEffect(() => {
    if (!bookingId) return;

    const collinsFeatures = venueGroupConfig?.collins_features;

    const fetchLeadBookerUrl = async () => {
      setIsLoadingLeadBooker(true);
      try {
        const url = await getCustomerPreOrderUrl(
          bookingId,
          true,
          collinsFeatures
        );
        setLeadBookerUrl(url);
      } catch (error) {
        console.error("Error fetching lead booker URL:", error);
      } finally {
        setIsLoadingLeadBooker(false);
      }
    };

    const fetchGroupMemberUrl = async () => {
      setIsLoadingGroupMember(true);
      try {
        const url = await getCustomerPreOrderUrl(
          bookingId,
          false,
          collinsFeatures
        );
        setGroupMemberUrl(url);
      } catch (error) {
        console.error("Error fetching group member URL:", error);
      } finally {
        setIsLoadingGroupMember(false);
      }
    };

    fetchLeadBookerUrl();
    fetchGroupMemberUrl();
  }, [bookingId, venueGroupConfig?.collins_features]);

  const handleOpenLeadBooker = () => {
    if (leadBookerUrl) {
      openExternalLink(leadBookerUrl);
    }
  };

  const handleOpenGroupMember = () => {
    if (groupMemberUrl) {
      openExternalLink(groupMemberUrl);
    }
  };

  if (!bookingId) {
    return null;
  }

  return (
    <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-4">
      <h4 className="text-sm font-medium">Checkout Links</h4>

      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="lead-booker-url" className="text-sm">
            Lead Booker Link
          </Label>
          <div className="flex items-center gap-2">
            {isLoadingLeadBooker ? (
              <div className="flex-1 flex items-center justify-center py-2">
                <Loader2
                  className="h-4 w-4 animate-spin"
                  aria-label="Loading lead booker link"
                />
              </div>
            ) : (
              <>
                <Input
                  id="lead-booker-url"
                  type="text"
                  value={leadBookerUrl}
                  readOnly
                  className="flex-1 text-xs"
                  aria-label="Lead booker checkout URL"
                />
                <EDSButton
                  variant="outline"
                  size="sm"
                  onClick={() => leadBookerClipboard.copy(leadBookerUrl)}
                  disabled={!leadBookerUrl}
                  className="min-w-[44px] min-h-[44px]"
                  aria-label={
                    leadBookerClipboard.copied
                      ? "Copied lead booker link"
                      : "Copy lead booker link"
                  }
                >
                  {leadBookerClipboard.copied ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Copy className="h-4 w-4" aria-hidden="true" />
                  )}
                </EDSButton>
                <EDSButton
                  variant="outline"
                  size="sm"
                  onClick={handleOpenLeadBooker}
                  disabled={!leadBookerUrl}
                  className="min-w-[44px] min-h-[44px]"
                  aria-label="Open lead booker link in new tab"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </EDSButton>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Send this link to the lead booker to manage the group order.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="group-member-url" className="text-sm">
            Group Member Link
          </Label>
          <div className="flex items-center gap-2">
            {isLoadingGroupMember ? (
              <div className="flex-1 flex items-center justify-center py-2">
                <Loader2
                  className="h-4 w-4 animate-spin"
                  aria-label="Loading group member link"
                />
              </div>
            ) : (
              <>
                <Input
                  id="group-member-url"
                  type="text"
                  value={groupMemberUrl}
                  readOnly
                  className="flex-1 text-xs"
                  aria-label="Group member checkout URL"
                />
                <EDSButton
                  variant="outline"
                  size="sm"
                  onClick={() => groupMemberClipboard.copy(groupMemberUrl)}
                  disabled={!groupMemberUrl}
                  className="min-w-[44px] min-h-[44px]"
                  aria-label={
                    groupMemberClipboard.copied
                      ? "Copied group member link"
                      : "Copy group member link"
                  }
                >
                  {groupMemberClipboard.copied ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Copy className="h-4 w-4" aria-hidden="true" />
                  )}
                </EDSButton>
                <EDSButton
                  variant="outline"
                  size="sm"
                  onClick={handleOpenGroupMember}
                  disabled={!groupMemberUrl}
                  className="min-w-[44px] min-h-[44px]"
                  aria-label="Open group member link in new tab"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </EDSButton>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Share this link with group members to place their individual orders.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerPreorderLinks;
